import React, { useCallback, useEffect, useRef, useState } from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import { useShallow } from "zustand/react/shallow";
import { TeamAccess } from "target_wasm";
import { AssetIcon, TEAM_ASSETS } from "@/components/common/AssetIcon";
import { AppearancePicker } from "@/components/common/AppearancePicker";
import { Button } from "@/components/common/Button";
import { TooltipButton } from "@/components/common/TooltipButton";
import { NumberCount } from "@/components/common/NumberCount";
import { Tooltip } from "@/components/common/Tooltip";
import { Inline, Stack } from "@/components/common/Stack";
import {
  ContextMenu,
  type ContextMenuItem,
} from "@/components/common/ContextMenu";
import { useContextMenu } from "@/components/hooks/useContextMenu";
import { LoadMoreButton } from "@/components/common/LoadMoreButton";
import { Spinner } from "@/components/common/Spinner";
import { EditableName } from "@/components/canvas/utils/EditableName";
import { DatasetMetaSection } from "@/components/main/datasets/DatasetMetaSection";
import { useNavigation } from "@/contexts/NavigationProvider";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { TeamDescription } from "./TeamDescription";
import { TeamMembersDialog } from "./TeamMembersDialog";
import { useTeamActions } from "./useTeamActions";
import type { TeamMember } from "./TeamsApi";

const EMPTY_MEMBERS: TeamMember[] = [];

interface TeamRowProps {
  team: TeamAccess;
  /// Controlled expansion so the page can offer "Collapse all". Members load
  /// lazily on first expand (the store no-ops if already fresh).
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

/// One team in the list. Collapsed it shows appearance / name / role;
/// expanded it lazily loads its members and shows the description +
/// member list. Clicking the name or icon enters the team; the hover pencil
/// renames (admin); hover actions manage members / leave / delete.
export const TeamRow: React.FC<TeamRowProps> = ({
  team,
  expanded,
  onExpandedChange,
}) => {
  const { navigateToTeam } = useNavigation();
  const iconRef = useRef<HTMLDivElement>(null);
  const nameRef = useRef<HTMLSpanElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isMembersOpen, setIsMembersOpen] = useState(false);
  const menu = useContextMenu();
  const [nameTruncated, setNameTruncated] = useState(false);

  const teamDetails = team.team_details_js();
  const teamId = team.team_id_str();
  const teamName = String(teamDetails.team_name_js());
  const teamColor = String(teamDetails.team_color_js());
  const teamAsset = String(teamDetails.team_asset_js());
  const roleLabel = String(team.relation_display_js());

  const { members, membersLoaded, hasMore, isLoadingMore } = useTeamsStore(
    useShallow((s) => ({
      members: s.teamMembers[teamId] ?? EMPTY_MEMBERS,
      membersLoaded: s.teamMembers[teamId] !== undefined,
      hasMore: Boolean(s.teamMembersToken[teamId]),
      isLoadingMore: s.teamMembersLoadingMore[teamId] ?? false,
    })),
  );
  const fetchTeamMembers = useTeamsStore((s) => s.fetchTeamMembers);
  const loadMoreTeamMembers = useTeamsStore((s) => s.loadMoreTeamMembers);

  const {
    permissions,
    commitName,
    editAppearance,
    requestLeave,
    requestDelete,
    dialogs,
  } = useTeamActions(team, members);
  const isAdmin = permissions.isAdmin;
  const memberCount = membersLoaded ? members.length : null;

  // Lazy-load members whenever expanded; the store no-ops if already fresh.
  useEffect(() => {
    if (expanded) void fetchTeamMembers(teamId);
  }, [expanded, teamId, fetchTeamMembers]);

  useEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    const check = () => setNameTruncated(el.scrollWidth > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [teamName]);

  const toggle = useCallback(
    () => onExpandedChange(!expanded),
    [expanded, onExpandedChange],
  );
  // Row-click enters the team (like the overview model row); ignored mid-rename
  // or while the appearance picker is open so those interactions don't navigate.
  const enterTeam = useCallback(() => {
    if (isEditing || isPickerOpen) return;
    navigateToTeam(teamId);
  }, [isEditing, isPickerOpen, navigateToTeam, teamId]);

  const contextMenuItems: ContextMenuItem[] = [
    {
      label: "Open",
      icon: <OpenInFullIcon className="icon-md" />,
      onClick: () => navigateToTeam(teamId),
    },
    {
      label: "Open in a new tab",
      icon: <OpenInNewIcon className="icon-md" />,
      onClick: () =>
        window.open(`/teams/${teamId}`, "_blank", "noopener,noreferrer"),
    },
    { label: "", onClick: () => {}, divider: true },
    {
      label: "Rename",
      icon: <EditOutlinedIcon className="icon-md" />,
      onClick: () => setIsEditing(true),
      disabled: !isAdmin,
    },
    {
      label: "Change appearance",
      icon: <PaletteOutlinedIcon className="icon-md" />,
      onClick: () => setIsPickerOpen(true),
      disabled: !isAdmin,
    },
    {
      label: "Manage members",
      icon: <PersonAddOutlinedIcon className="icon-md" />,
      onClick: () => setIsMembersOpen(true),
      disabled: !isAdmin,
    },
    { label: "", onClick: () => {}, divider: true },
    {
      label: "Leave",
      icon: <LogoutRoundedIcon className="icon-md" />,
      onClick: requestLeave,
    },
    {
      label: "Delete",
      icon: <DeleteOutlineOutlinedIcon className="icon-md" />,
      onClick: requestDelete,
      disabled: !permissions.canDelete,
      danger: true,
    },
  ];

  return (
    <div className="rounded-app group/card">
      <div
        className={`group flex items-center gap-2 px-1 py-1.5 rounded-app select-none cursor-pointer group-hover/card:bg-bg-hover transition-colors ${
          menu.isOpen ? "bg-bg-hover" : ""
        }`}
        tabIndex={0}
        onClick={enterTeam}
        {...menu.triggerProps}
      >
        <Button
          variant="default"
          size="xs"
          aria-label={expanded ? `Collapse ${teamName}` : `Expand ${teamName}`}
          onClick={(e) => {
            e.stopPropagation();
            toggle();
          }}
        >
          {expanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
        </Button>

        <div className="relative shrink-0" ref={iconRef}>
          <span
            role="button"
            aria-label={isAdmin ? "Change team appearance" : `Open ${teamName}`}
            className={`flex items-center justify-center rounded-app p-0.5 cursor-pointer ${isAdmin ? "hover:bg-bg-hover" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              if (isAdmin) setIsPickerOpen(true);
              else enterTeam();
            }}
          >
            <AssetIcon size="sm" color={teamColor} asset={teamAsset} />
          </span>
          <AppearancePicker
            currentColor={teamColor}
            currentAsset={teamAsset}
            assets={TEAM_ASSETS}
            onColorChange={(color) => editAppearance({ color })}
            onAssetChange={(asset) => editAppearance({ asset })}
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            disabled={!isAdmin}
            anchorRef={iconRef}
          />
        </div>

        <div className="min-w-0 flex-1">
          <Inline gap="xs" className="min-w-0 items-center">
            <Tooltip title={nameTruncated ? teamName : ""} placement="top">
              <span
                ref={nameRef}
                className="min-w-0 truncate text-fg"
                style={{
                  fontSize: "var(--font-size)",
                  fontWeight: "var(--font-weight-medium)",
                }}
              >
                <EditableName
                  name={teamName || "Untitled team"}
                  isEditing={isEditing}
                  canEdit={isAdmin}
                  editTrigger="icon"
                  editColor={teamColor || undefined}
                  setIsEditing={setIsEditing}
                  update={() => {}}
                  commit={commitName}
                  abort={() => setIsEditing(false)}
                />
              </span>
            </Tooltip>
            <span
              className="text-small text-fg-muted shrink-0"
              style={{ fontWeight: "var(--font-weight-regular)" }}
            >
              {roleLabel}
            </span>
          </Inline>
        </div>

        <div
          className={`flex items-center gap-2 shrink-0 transition-opacity ${
            menu.isOpen
              ? ""
              : "opacity-0 group-hover/card:opacity-100 focus-within:opacity-100"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {isAdmin && (
            <TooltipButton
              title="Manage members"
              variant="contextual"
              size="xs"
              aria-label={`Manage members of ${teamName}`}
              onClick={() => setIsMembersOpen(true)}
            >
              <PersonAddOutlinedIcon />
            </TooltipButton>
          )}
        </div>
      </div>

      <div
        className="grid"
        style={{
          gridTemplateRows: expanded ? "1fr" : "0fr",
          transition:
            "grid-template-rows var(--duration-short) var(--ease-move)",
        }}
      >
        <div className="overflow-hidden" style={{ minHeight: 0 }}>
          <div className="pb-2 cursor-default">
            {/* Default pl-8/pl-10 keeps Description aligned with Members as a
                subsection; pr-0 lines its "+ Add a description" pill up with the
                page's flush search bar / New team footer. */}
            <TeamDescription teamId={teamId} canEdit={isAdmin} pr="pr-0" />
            <DatasetMetaSection
              icon={<PeopleOutlinedIcon className="icon-sm" />}
              label="Members"
              meta={
                memberCount !== null ? (
                  <NumberCount count={memberCount} size="sm" />
                ) : undefined
              }
            />
            {!membersLoaded ? (
              <div className="flex items-center gap-2 pl-16 py-2 text-small text-fg-muted">
                <Spinner className="icon-sm" /> Loading members…
              </div>
            ) : (
              <Stack gap="2xs">
                {members.map((m) => {
                  const name = String(
                    m.user.user_name_js() ||
                      m.user.user_email_js() ||
                      "Unknown",
                  );
                  const email = String(m.user.user_email_js() || "");
                  return (
                    <div
                      key={m.user.user_id_js()}
                      className="flex items-center gap-2 ml-14 px-2 rounded-app item-height select-none hover:bg-bg-hover transition-colors"
                    >
                      <Tooltip title={email} placement="top">
                        <span
                          className="truncate min-w-0 text-fg"
                          style={{ fontSize: "var(--font-size)" }}
                        >
                          {name}
                        </span>
                      </Tooltip>
                      {m.isCurrentUser && !m.isPending && (
                        <span
                          className="text-small text-fg-muted shrink-0"
                          style={{ fontWeight: "var(--font-weight-medium)" }}
                        >
                          (You)
                        </span>
                      )}
                      <span
                        className="text-small text-fg-muted ml-auto shrink-0"
                        style={{ fontWeight: "var(--font-weight-regular)" }}
                      >
                        {m.isPending ? "Invited" : m.accessLevel}
                      </span>
                    </div>
                  );
                })}
                {hasMore && (
                  <div className="pl-16 pr-2">
                    <LoadMoreButton
                      onClick={() => loadMoreTeamMembers(teamId)}
                      isLoading={isLoadingMore}
                    />
                  </div>
                )}
              </Stack>
            )}
          </div>
        </div>
      </div>

      <ContextMenu
        items={contextMenuItems}
        position={menu.position}
        onClose={menu.close}
        ariaLabel={`Actions for ${teamName || "Untitled team"}`}
      />

      {dialogs}

      {isMembersOpen && (
        <TeamMembersDialog
          open={isMembersOpen}
          onClose={() => setIsMembersOpen(false)}
          teamId={teamId}
          teamName={teamName}
          isAdmin={isAdmin}
        />
      )}
    </div>
  );
};
